import { Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TreeNode } from 'primeng/api';
import { DateUtil } from '../../DateUtil';
import { TreeNodeXEVPRMImportedMessage } from '../WebTraderNode/Entity/TreeNodeXEVPRMImportedMessage';
import { Art57Node } from './Art57Node';
import { TreeNodeXEVPRMImportedAck } from '../WebTraderNode/Entity/TreeNodeXEVPRMImportedAck';
import { BaseNode } from '../BaseNode';
import { AuthService } from '../../../service/auth-service/auth-service.service';
import { LookupGetService } from '../../../service/lookup-service/lookup-get.service';

export class Art57NodeUtils {


  static findChildNodeOfType<T extends BaseNode>(nodes: BaseNode[], type: new (...args: any) => T): BaseNode | null {
    return nodes!.find(i => i instanceof type) as BaseNode;
  }


  static findFirstChildNodeOfType<T extends TreeNode>(root: TreeNode | undefined, type: new (...args: any[]) => T): TreeNode | null {
    if (!root) {
      return null;
    }

    const stack: TreeNode[] = [root];

    while (stack.length > 0) {
      const node = stack.pop();

      if (node instanceof type) {
        return node;
      }

      if (node && node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          stack.push(node.children[i]);
        }
      }
    }

    return null;
  }

  static findFirstParentNodeOfType<T extends TreeNode>(node: TreeNode, type: new (...args: any[]) => T): TreeNode | null {
    if (!node) {
      return null;
    } else if (node.parent && node.parent instanceof type) {
      return node.parent;
    } else {
      return this.findFirstParentNodeOfType(node.parent!, type);
    }

  }

  static findFirstCurrentOrParentNodeOfType<T extends TreeNode>(node: TreeNode, type: new (...args: any[]) => T): TreeNode | null {
    if (!node) {
      return null;
    } else if (node instanceof type) {
      return node;
    } else {
      return this.findFirstParentNodeOfType(node, type);
    }
  }

  /**
   * 
   * 
   * 
   * @param fields Formly fields for a given node  (Input parameter)
   * @param jsonObject Unmarshalled json object for a given node (Input parameter)
   * @param model Formly model for a given node (Input/Output parameter)
   * @param parentKey Parent key for a given node (Input only for corner cases)
   */
  static generateFormlyModel(fields: FormlyFieldConfig[],
    jsonObject: any,
    model: any,
    parentKey: string = '') {

    //For all node formly fields
    for (let field of fields) {

      //If the field has a fieldGroup then iterate recursively
      if (field.fieldGroup) {

        // If key Field Group is found in jsonObject then call recursively
        if ((field.key)
          && (jsonObject)
          && (jsonObject instanceof Object)
          && (String(field.key) in jsonObject)
          && (field.fieldGroup[0].key)
          && (!(String(field.fieldGroup[0].key) in jsonObject))
          && jsonObject[String(field.key)]) {

          let childModel = { [String(field.key)]: {} };
          this.generateFormlyModel(field.fieldGroup, jsonObject[String(field.key)], childModel, String(field.key));

          // Update also the model object
          if (parentKey != '') {
            if (!model[parentKey]) {
              model[parentKey] = {};
            }

            // If the lookup value is null the json member must be ignored
            if ((jsonObject[String(field.key)].hasOwnProperty("value")) &&
              (jsonObject[String(field.key)]["value"] != null)) {

              if (!model[parentKey][String(field.key)]) {
                model[parentKey][String(field.key)] = {};
              }
              Object.assign(model[parentKey][String(field.key)], childModel[String(field.key)]);

            }

          } else {
            Object.assign(model, { [String(field.key)]: childModel[String(field.key)] });
          }
          // CORNER CASE parentKey provided  
        } else if ((parentKey != '')
          && jsonObject
          && jsonObject.hasOwnProperty(parentKey)) {

          if (!model[parentKey]) {
            model[parentKey] = {};
          }
          let childModel = { [parentKey]: {} };
          this.generateFormlyModel(field.fieldGroup, jsonObject[parentKey], childModel, parentKey);

          // Update also the model object
          Object.assign(model[parentKey], childModel[parentKey]);

          // CORNER CASE parent key not provided (retrived from field) 
        } else if ((field.key)
          && (jsonObject)
          && (jsonObject.hasOwnProperty(String(field.key)))
          && (String(field.key) in jsonObject)
          && jsonObject[String(field.key)] != null) {

          // create parented Key for root fieldGroups lookups          
          parentKey = String(field.key);
          if (!model[parentKey]) {
            model[parentKey] = {};
          }
          let childModel = { [parentKey]: {} };
          this.generateFormlyModel(field.fieldGroup, jsonObject[parentKey], childModel, parentKey);

          // Update also the model object
          Object.assign(model[parentKey], childModel[parentKey]);
          parentKey = ''; // JMBL - TESTING - after all recursive calls with parent key we remove this value for next fields processing 

          // Invoke recursively with no refinement 
        } else {
          this.generateFormlyModel(field.fieldGroup, jsonObject, model, parentKey);
        }


        //If it is a formly field.  Exit condition 
      } else if (field.key
        && typeof field.key === 'string'
        && jsonObject
        && jsonObject.hasOwnProperty(field.key)
        && (jsonObject[field.key] != null)) {

        if (parentKey != '') {


          if (!model[parentKey]) {
            model[parentKey] = {};
          }
          model[parentKey][field.key] = jsonObject[field.key];


        } else {
          model[field.key] = jsonObject[field.key];
        }

      }
    }

  }

  static async createAckxevmpd(jsonObject: any, injector: Injector): Promise<TreeNodeXEVPRMImportedAck> {
    const messageNode = new TreeNodeXEVPRMImportedAck(injector);
    this.generateFormlyModel(messageNode.data.fields, jsonObject.ichicsrmessageheader, messageNode.data.model, '');
    if (messageNode.data.model.messagedate && messageNode.data.model.messagedateformat) {
      messageNode.data.model.messagedate = DateUtil.getFormatedDate(messageNode.data.model.messagedateformat, messageNode.data.model.messagedate);
    }
    messageNode.setMessage(jsonObject);
    messageNode.refreshLabel(true);
    return messageNode;
  }


  static async createXEVPRM(jsonObject: any, injector: Injector): Promise<TreeNodeXEVPRMImportedMessage> {
    const messageNode = new TreeNodeXEVPRMImportedMessage(injector);

    //Generate formly model from json object
    this.generateFormlyModel(messageNode.data.fields, jsonObject.ichicsrmessageheader, messageNode.data.model, '');

    // Fix Date field
    if (messageNode.data.model.messagedate && messageNode.data.model.messagedateformat) {
      messageNode.data.model.messagedate = DateUtil.getFormatedDate(messageNode.data.model.messagedateformat, messageNode.data.model.messagedate);
    }
    messageNode.setMessage(jsonObject);

    messageNode.refreshLabel(true);

    return messageNode;
  }

  static importNodeType(rootNode: TreeNode, jsonObject: any, section: string, type: new (...args: any[]) => Art57Node, params?: any) {

    if (jsonObject && jsonObject[section]) {
      const nodeList: TreeNode | null = this.findFirstChildNodeOfType(rootNode, type);
      for (let jsonNodeInfo of jsonObject[section]) {
        if (params) {
          params["operationType"] = jsonNodeInfo.operationtype;
        }
        const nodeType: Art57Node = (nodeList as Art57Node).createChildTemplate(params)![0];
        nodeType.setMessage(jsonNodeInfo);
        nodeType.refreshLabel();
      }
      (nodeList as Art57Node).refreshLabel();
    }

  }

  static findAscendantObjectWithDataProperty(obj: any, propertyName: string): any | null {
    if (obj.data && obj.data.model && obj.data.model.hasOwnProperty(propertyName)) {
      return obj;
    } else if (obj.parent) {
      return this.findAscendantObjectWithDataProperty(obj.parent, propertyName);
    } else {
      return null;
    }
  }

  static findAscendantObjectWithProperty(obj: any, propertyName: string): any | null {
    if (obj.hasOwnProperty(propertyName)) {
      return obj;
    } else if (obj.parent) {
      return this.findAscendantObjectWithProperty(obj.parent, propertyName);
    } else {
      return null;
    }
  }

  static isValidStatus(injector: Injector, code: number): boolean {
    const result = injector.get(LookupGetService).retrieveValueFromCache("validauthorisationstatus", code);
    return result != '';
  }

  static nodeCanBeLoaded(injector: Injector, operation: number, data: any, errorMessage: any): boolean {

    //Reinsert operation
    if (operation == 1) {
      return true;
    }

    // if type_term exists and is equals 2 or 3 operation is not allowed
    if (data.hasOwnProperty('type_Term')) {
      if (data.type_Term == 2 || data.type_Term == 3) {
        errorMessage.message = errorMessage.message + '\n\n' + 'Inserts and amendments of proposed or standard terms should be requested via a change request submitted via the Referentials Management Systems portal';
        return false;
      }
    }

    //Nullification operation
    if (operation == 4) {

      //Record come from Load or Advanced queries
      let authorisationstatus = data.authorisationstatus && data.authorisationstatus.value ? data.authorisationstatus && data.authorisationstatus.value :
        data.authorisationstatus ? data.authorisationstatus :
          undefined;
      
      //AMPs
      if (authorisationstatus) {

        //It is an authorised product
        if ((injector.get(AuthService).getPrincipal().organisation.organisationIdHQ === data.ownerhqid) &&
          (!injector.get(AuthService).isEMAOrganisation()) &&
          (data.stamp_nullif == null) &&
          (!data.validated) &&
          (this.isValidStatus(injector, authorisationstatus))) {
          return true;
        } else if (injector.get(AuthService).isEMAOrganisation() &&
          (data.stamp_nullif == null)) {
          return true;
        } else {
          return false;
        }

      //Any other entities
      } else {

        //It is not an authorised product
        if ((injector.get(AuthService).getPrincipal().organisation.organisationIdHQ === data.ownerhqid) &&
          (data.stamp_nullif == null) &&
          ((!data.validated) || (data.type_product == 1))) {
          return true;
        } else if (injector.get(AuthService).isEMAOrganisation() &&
          (data.stamp_nullif == null)) {
          return true;
        } else {
          return false;
        }

      }

    }

    //Invalidation operation
    if (operation == 6) {

      //Record come from Load or Advanced queries
      let authorisationstatus = data.authorisationstatus && data.authorisationstatus.value ? data.authorisationstatus && data.authorisationstatus.value :
        data.authorisationstatus ? data.authorisationstatus :
          undefined;

      return (((injector.get(AuthService).getPrincipal().organisation.organisationIdHQ === data.ownerhqid)) || (injector.get(AuthService).isEMAOrganisation())
      ) && data.stamp_nullif == null
        && (this.isValidStatus(injector, authorisationstatus) || injector.get(AuthService).isEMAOrganisation());

    }

    //Update operation
    if (operation == 2) {

      //Record come from Load or Advanced queries
      let authorisationstatus = data.authorisationstatus && data.authorisationstatus.value ? data.authorisationstatus && data.authorisationstatus.value :
        data.authorisationstatus ? data.authorisationstatus :
          undefined;

      //AMPs
      if (authorisationstatus) {

        //It is an authorised product
        if ((injector.get(AuthService).getPrincipal().organisation.organisationIdHQ === data.ownerhqid) &&
          (!injector.get(AuthService).isEMAOrganisation()) &&
          (data.stamp_nullif == null) &&
          (this.isValidStatus(injector, authorisationstatus))) {
          return true;
        } else if (injector.get(AuthService).isEMAOrganisation() &&
          (data.stamp_nullif == null)) {
          return true;
        } else {
          return false;
        }

      //Any other entities
      } else {

        //It is not an authorised product
        if ((injector.get(AuthService).getPrincipal().organisation.organisationIdHQ === data.ownerhqid) &&
          (data.stamp_nullif == null)) {
          return true;
        } else if (injector.get(AuthService).isEMAOrganisation() &&
          (data.stamp_nullif == null)) {
          return true;
        } else {
          return false;
        }

      }

    }

    return false;
  }

}
